"use client";

import Link from "next/link";
import type { Destination } from "./quiz-data";
import { exploreGuidePathBySlug } from "@/lib/explore/guide-paths";
import { trackEvent } from "@/lib/analytics";

type Props = {
  rank: number;
  destination: Destination;
  matchPercent: number;
  liveJobs: number;
  pitch: string;
};

export function ResultCard({ rank, destination, matchPercent, liveJobs, pitch }: Props) {
  const isTop = rank === 1;
  return (
    <article
      className={[
        "bg-white rounded-[18px] border overflow-hidden transition-all",
        isTop
          ? "border-gold/40 shadow-[0_12px_40px_rgba(28, 138, 153,0.14)]"
          : "border-border-light",
      ].join(" ")}
      style={{ animation: `fadeUp 0.5s ease-out ${(rank - 1) * 0.12}s backwards` }}
    >
      <div
        className="relative h-28 md:h-32 p-5 flex items-end"
        style={{ background: destination.gradient }}
      >
        <div className="absolute top-4 left-5 text-white/90 text-[10px] font-semibold uppercase tracking-[2px]">
          {rank === 1 ? "Your top match" : `#${rank} match`}
        </div>
        <div className="flex items-end justify-between w-full relative z-10">
          <div>
            <span
              className="text-3xl md:text-4xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
              aria-hidden
            >
              {destination.emoji}
            </span>
          </div>
          <div className="text-right">
            <div className="text-white font-serif text-[36px] md:text-[44px] leading-none">
              {matchPercent}%
            </div>
            <div className="text-white/80 text-[10px] uppercase tracking-[1.5px] mt-1">
              Match
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-7">
        <h3 className="font-serif text-[24px] md:text-[28px] text-navy mb-1">
          {destination.name}
        </h3>
        <p className="text-[12px] uppercase tracking-[1px] text-gold font-semibold mb-3">
          {destination.region}
        </p>
        <p className="text-[14.5px] md:text-[15px] text-text-body leading-[1.6] mb-5">
          {pitch}
        </p>

        <div className="flex flex-wrap gap-2 mb-5">
          <Link
            href={exploreGuidePathBySlug(destination.guideSlug) ?? `/guides/${destination.guideSlug}`}
            onClick={() => trackEvent("destination_quiz_result_clicked", { destination: destination.id, cta: "guide" })}
            className="px-3.5 py-2 text-[12.5px] font-medium rounded-full bg-cream text-charcoal no-underline hover:bg-gold/10 hover:text-gold transition-colors"
          >
            Read the guide →
          </Link>
          {destination.conditionsSlug ? (
            <Link
              href={`/conditions/${destination.conditionsSlug}`}
              className="px-3.5 py-2 text-[12.5px] font-medium rounded-full bg-cream text-charcoal no-underline hover:bg-gold/10 hover:text-gold transition-colors"
            >
              Live conditions →
            </Link>
          ) : null}
          <Link
            href={`/jobs?${destination.jobsQuery}`}
            onClick={() => trackEvent("destination_quiz_result_clicked", { destination: destination.id, cta: "jobs" })}
            className="px-3.5 py-2 text-[12.5px] font-medium rounded-full bg-navy text-white no-underline hover:bg-navy-hover transition-colors"
          >
            {liveJobs > 0 ? `${liveJobs} live job${liveJobs === 1 ? "" : "s"} →` : "Browse jobs →"}
          </Link>
        </div>
      </div>
    </article>
  );
}
